import React from 'react';

import { Divider, Grid, Header } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';
import ShowDiffContent from '@/components/Common/ShowDiffContent';
import { useUpdateConvention } from '@/hooks';
import {
  composeLibelleVoie,
  extractLibelleVoie,
  formatDate,
  formatLieu,
  normalizeConventionCodes,
  resolveCasCode,
} from '@/utils';
import { OPTIONS_ATTESTATION_LIEN_PARENTE } from '@/constants';

import InformationsDeclarationAttestationsModal from './InformationsDeclarationAttestationsModal';
import InformationsDeclarationSignatureModal from './InformationsDeclarationSignatureModal';

interface InformationsDeclarationInitialeProps {
  showDiff?: boolean;
  conventionDeclaration: any;
  onSaved?: () => void;
}

// Sous-sections éditables de ce panneau.
type DeclarationSection = 'attestations' | 'signature';

const formatAdresseCommune = (adresse: any): string => {
  const rue =
    [
      adresse?.numeroVoie,
      // Complète les libellés enregistrés sans le type de voie du référentiel.
      composeLibelleVoie(adresse?.typeVoie, adresse?.libelleVoie),
    ]
      .map((value) => (value ?? '').toString().trim())
      // ETCV enregistre 0 en numéro de voie quand il n'y en a pas.
      .filter((value) => value !== '' && value !== '0')
      .join(' ') ||
    // Repli sur la ligne `adresse` sans son « 0 » ni son « CP LOCALITÉ »,
    // déjà affichés par formatLieu.
    (extractLibelleVoie(adresse?.adresse, adresse?.numeroVoie) ?? '');

  const lieu = formatLieu(
    adresse?.localite,
    adresse?.codePostal,
    adresse?.libellePays,
    '',
  );

  return [rue, lieu].filter(Boolean).join(' ') || 'Non renseignée';
};

const InformationsDeclarationInitiale = (
  props: InformationsDeclarationInitialeProps,
): JSX.Element => {
  const { conventionDeclaration, showDiff, onSaved } = props;

  const { updateConvention } = useUpdateConvention();

  const [openSection, setOpenSection] =
    React.useState<DeclarationSection | null>(null);
  const [infoDeclaIni, setInfoDeclaIni] = React.useState<any>(null);

  const handleOpenAttestations = React.useCallback(() => {
    // Le GET renvoie le libellé ; on le convertit en clé (0/1) pour le PUT.
    setInfoDeclaIni({
      attestationLienParente: resolveCasCode(
        OPTIONS_ATTESTATION_LIEN_PARENTE,
        conventionDeclaration?.attestationLienParente,
      ),
      attestationAdresseCommune:
        conventionDeclaration?.attestationAdresseCommune ?? 0,
      adresseCommune: { ...conventionDeclaration?.adresseCommune },
    });
    setOpenSection('attestations');
  }, [conventionDeclaration]);

  const handleOpenSignature = React.useCallback(() => {
    setInfoDeclaIni({
      dateDeclaration: conventionDeclaration?.dateDeclaration ?? null,
      adresseDeclaration: { ...conventionDeclaration?.adresseDeclaration },
    });
    setOpenSection('signature');
  }, [conventionDeclaration]);

  const handleChange = React.useCallback((data: Record<string, unknown>) => {
    setInfoDeclaIni((prev: any) => ({ ...prev, ...data }));
  }, []);

  const handleClose = React.useCallback(() => {
    setOpenSection(null);
    setInfoDeclaIni(null);
  }, []);

  const handleSave = React.useCallback(() => {
    if (!conventionDeclaration?.identifiant) {
      return Promise.reject(
        new Error('Contexte de convention incomplet pour la sauvegarde'),
      );
    }

    const payload = {
      ...normalizeConventionCodes(conventionDeclaration),
      ...infoDeclaIni,
    };

    return updateConvention(conventionDeclaration.identifiant, payload).then(
      () => {
        onSaved?.();
      },
    );
  }, [conventionDeclaration, infoDeclaIni, updateConvention, onSaved]);

  const adresseCommune = formatAdresseCommune(
    conventionDeclaration?.adresseCommune,
  );
  const prevAdresseCommune = formatAdresseCommune(
    conventionDeclaration?.previousConvention?.adresseCommune,
  );
  const adresseDeclaration = formatLieu(
    conventionDeclaration?.adresseDeclaration?.localite,
    conventionDeclaration?.adresseDeclaration?.codePostal,
    conventionDeclaration?.adresseDeclaration?.libellePays,
    'Non renseignée',
  );
  const prevAdresseDeclaration = formatLieu(
    conventionDeclaration?.previousConvention?.adresseDeclaration?.localite,
    conventionDeclaration?.previousConvention?.adresseDeclaration?.codePostal,
    conventionDeclaration?.previousConvention?.adresseDeclaration?.libellePays,
    'Non renseignée',
  );

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            ATTESTATIONS
          </Header>
          <PanelButtonEdition
            onClick={handleOpenAttestations}
            data-testid="panel-edit-modal-declaration-attestations"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={10}>
          {showDiff ? (
            <>
              <span>
                <b>Lien de parenté</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention
                    ?.attestationLienParente
                }
                newContent={conventionDeclaration?.attestationLienParente}
              />
            </>
          ) : (
            <>
              <span>
                <b>Lien de parenté : </b>
              </span>
              <span>{conventionDeclaration?.attestationLienParente}</span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={7}>
          {showDiff ? (
            <>
              <span>
                <b>Résidence commune</b>
              </span>
              <ShowDiffContent
                oldContent={prevAdresseCommune}
                newContent={adresseCommune}
              />
            </>
          ) : (
            <>
              <span>
                <b>Résidence commune : </b>
              </span>
              <span>{adresseCommune}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.adresseCommune
                    ?.communeDelegue || 'Non renseignée'
                }
                newContent={
                  conventionDeclaration?.adresseCommune?.communeDelegue ||
                  'Non renseignée'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {conventionDeclaration?.adresseCommune?.communeDelegue ||
                  'Non renseignée'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            SIGNATURE
          </Header>
          <PanelButtonEdition
            onClick={handleOpenSignature}
            data-testid="panel-edit-modal-declaration-signature"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3}>
          {showDiff ? (
            <>
              <span>
                <b>Déclaration signée le</b>
              </span>
              <ShowDiffContent
                oldContent={formatDate(
                  conventionDeclaration?.previousConvention?.dateDeclaration,
                )}
                newContent={formatDate(conventionDeclaration?.dateDeclaration)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Déclaration signée le : </b>
              </span>
              <span>{formatDate(conventionDeclaration?.dateDeclaration)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={4}>
          {showDiff ? (
            <>
              <span>
                <b>À</b>
              </span>
              <ShowDiffContent
                oldContent={prevAdresseDeclaration}
                newContent={adresseDeclaration}
              />
            </>
          ) : (
            <>
              <span>
                <b>À : </b>
              </span>
              <span>{adresseDeclaration}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.adresseDeclaration
                    ?.communeDelegue || 'Non renseignée'
                }
                newContent={
                  conventionDeclaration?.adresseDeclaration?.communeDelegue ||
                  'Non renseignée'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {conventionDeclaration?.adresseDeclaration?.communeDelegue ||
                  'Non renseignée'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      {openSection === 'attestations' && (
        <InformationsDeclarationAttestationsModal
          data={infoDeclaIni}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleClose}
          onCancel={handleClose}
        />
      )}
      {openSection === 'signature' && (
        <InformationsDeclarationSignatureModal
          data={infoDeclaIni}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleClose}
          onCancel={handleClose}
        />
      )}
    </Grid>
  );
};

export default InformationsDeclarationInitiale;
